import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import {
  Users, Wrench, Cube, ClipboardText, ArrowRight, Warning, CircleNotch,
  Briefcase, CurrencyDollar, MapPin, CalendarBlank, Clock,
} from "@phosphor-icons/react";

function RateLimitBanner({ usage, jobsSource }) {
  if (!usage || !usage.known) return null;
  if (!usage.limited && !usage.low) return null;
  const limited = usage.limited;
  const resetTxt = usage.reset_at ? new Date(usage.reset_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null;
  return (
    <div data-testid="aroflo-rate-banner"
      className={`mt-6 flex items-start gap-3 rounded-sm border px-4 py-3 ${limited ? "border-[#D97706]/40 bg-[#D97706]/5" : "border-[#0EA5E9]/40 bg-[#0EA5E9]/5"}`}>
      <Warning size={20} weight="fill" className={limited ? "mt-0.5 shrink-0 text-[#D97706]" : "mt-0.5 shrink-0 text-[#0EA5E9]"} />
      <div className="text-sm text-neutral-700">
        {limited ? (
          <>
            <span className="font-semibold text-[#0A0A0A]">Aroflo v2 daily API limit reached</span> ({usage.daily_used?.toLocaleString()}/{usage.daily_limit?.toLocaleString()} requests).
            {jobsSource === "aroflo_v1" && <> Job boards are showing <span className="font-semibold">live Aroflo v1 (legacy)</span> data instead.</>}
            {usage.reset_in_hours ? <> Full v2 access resumes in ~{usage.reset_in_hours}h{resetTxt ? ` (around ${resetTxt})` : ""}.</> : null}
          </>
        ) : (
          <><span className="font-semibold text-[#0A0A0A]">Aroflo v2 API usage is high</span> — {usage.daily_used?.toLocaleString()} of {usage.daily_limit?.toLocaleString()} daily requests used. Heavy syncing may hit the limit.</>
        )}
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent, onClick, testid, ranged }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="group relative flex items-center gap-4 border border-neutral-200 bg-white p-5 text-left shadow-sm rounded-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      {ranged && <span className="absolute right-2 top-2"><RangeBadge /></span>}
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-sm ${accent.bg}`}>
        <Icon size={24} weight="bold" className={accent.text} />
      </div>
      <div>
        <p className="font-heading text-3xl font-black tracking-tighter text-[#0A0A0A]">{value}</p>
        <p className="text-xs uppercase tracking-widest font-semibold text-neutral-500">{label}</p>
      </div>
    </button>
  );
}

const fmtMoney = (v) => "$" + Math.round(v || 0).toLocaleString();

function ClientName({ r, navigate }) {
  if (r.client_id)
    return <button onClick={() => navigate(`/clients/${r.client_id}`)} title={r.client}
      className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-[#0055FF] hover:underline">{r.client}</button>;
  return <span className="min-w-0 flex-1 truncate text-sm text-[#0A0A0A]" title={r.client}>{r.client}</span>;
}

function TechName({ r, navigate }) {
  if (r.technician_id)
    return <button onClick={() => navigate(`/technicians/${r.technician_id}`)} title={r.technician}
      className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-[#0055FF] hover:underline">{r.technician}</button>;
  return <span className="min-w-0 flex-1 truncate text-sm text-[#0A0A0A]" title={r.technician}>{r.technician}</span>;
}

function Leaderboard({ title, icon: Icon, rows, render, metric, navigate, ranged }) {
  return (
    <div className="flex flex-col border border-neutral-200 bg-white shadow-sm rounded-sm" data-testid={`board-${metric}`}>
      <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
        <div className="flex items-center gap-2">
          <Icon size={18} weight="bold" className="text-[#0055FF]" />
          <h2 className="font-heading text-base font-black tracking-tight text-[#0A0A0A]">{title}</h2>
          {ranged && <RangeBadge />}
        </div>
        <button data-testid={`board-${metric}-viewall`} onClick={() => navigate(`/leaderboard/${metric}`)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#0055FF] hover:underline">
          View full list <ArrowRight size={13} weight="bold" />
        </button>
      </div>
      <ol className="flex-1 divide-y divide-neutral-100">
        {rows.map((r, i) => (
          <li key={i} className="flex items-center gap-3 px-5 py-2.5" data-testid={`board-${metric}-row-${i}`}>
            <span className="w-5 shrink-0 text-sm font-bold text-neutral-400">{i + 1}</span>
            {render(r)}
          </li>
        ))}
        {rows.length === 0 && <li className="px-5 py-6 text-center text-sm text-neutral-400">No data yet.</li>}
      </ol>
    </div>
  );
}

const BAND = {
  Critical: "bg-[#DC2626]/10 text-[#DC2626]",
  High: "bg-[#D97706]/10 text-[#D97706]",
  Monitor: "bg-[#0055FF]/10 text-[#0055FF]",
  Healthy: "bg-[#059669]/10 text-[#059669]",
};

// --- date helpers ---
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const daysAgo = (n) => { const t = new Date(); t.setDate(t.getDate() - n); return t; };
const monthsAgoStart = (n) => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth() - n, 1); };
const endOfThisMonth = () => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth() + 1, 0); };
const startOfYear = () => { const t = new Date(); return new Date(t.getFullYear(), 0, 1); };

const PRESETS = [
  { id: "7d", label: "7 days", range: () => [ymd(daysAgo(6)), ymd(new Date())] },
  { id: "30d", label: "30 days", range: () => [ymd(daysAgo(29)), ymd(new Date())] },
  { id: "90d", label: "90 days", range: () => [ymd(daysAgo(89)), ymd(new Date())] },
  { id: "12m", label: "12 months", range: () => [ymd(monthsAgoStart(11)), ymd(endOfThisMonth())] },
  { id: "24m", label: "24 months", range: () => [ymd(monthsAgoStart(23)), ymd(endOfThisMonth())] },
  { id: "36m", label: "36 months", range: () => [ymd(monthsAgoStart(35)), ymd(endOfThisMonth())] },
  { id: "ytd", label: "This year", range: () => [ymd(startOfYear()), ymd(new Date())] },
  { id: "all", label: "All time", range: () => ["", ""] },
];

const fmtDisplay = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);

const RangeBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-sm bg-[#0055FF]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#0055FF]" title="Reflects the selected date range">
    <CalendarBlank size={10} weight="bold" /> In range
  </span>
);

function coverageLabel(c) {
  if (!c || !c.from) return null;
  const d = new Date(c.from.replace(/\//g, "-"));
  if (isNaN(d)) return null;
  return d.toLocaleDateString([], { month: "short", year: "numeric" });
}

function DateRangeBar({ preset, setPreset, start, end, setStart, setEnd, jobField, setJobField, loading, coverage }) {
  const apply = (p) => { const [s, e] = p.range(); setStart(s); setEnd(e); setPreset(p.id); };
  const cov = coverageLabel(coverage);
  return (
    <div className="mt-6 flex flex-col gap-3 rounded-sm border border-neutral-200 bg-white p-4 shadow-sm" data-testid="dashboard-daterange">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
          <CalendarBlank size={18} weight="bold" className="text-[#0055FF]" />
          Date range
          {loading && <CircleNotch size={14} className="animate-spin text-[#0055FF]" />}
          {cov && (
            <span data-testid="data-coverage" title={`Earliest record we hold${coverage?.timesheets_count ? ` · ${coverage.timesheets_count.toLocaleString()} timesheets` : ""}${coverage?.jobs_count ? `, ${coverage.jobs_count.toLocaleString()} jobs` : ""}`}
              className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#059669]/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-[#059669]">
              <Clock size={11} weight="bold" /> Data since {cov}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Jobs by</span>
          <div className="inline-flex overflow-hidden rounded-sm border border-neutral-300" data-testid="jobfield-toggle">
            {[{ id: "requested", label: "Requested" }, { id: "due", label: "Due" }].map((o) => (
              <button key={o.id} data-testid={`jobfield-${o.id}`} onClick={() => setJobField(o.id)}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold transition-colors ${jobField === o.id ? "bg-[#0A0A0A] text-white" : "bg-white text-neutral-600 hover:bg-neutral-100"}`}>
                <Clock size={12} weight="bold" />{o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => (
          <button key={p.id} data-testid={`preset-${p.id}`} onClick={() => apply(p)}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-all duration-200 ${preset === p.id ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-neutral-300 bg-white text-neutral-700 hover:border-[#0055FF]"}`}>
            {p.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <input type="date" data-testid="range-start" value={start} max={end || undefined}
            onChange={(e) => { setStart(e.target.value); setPreset("custom"); }}
            className="rounded-sm border border-neutral-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-[#0055FF]" />
          <span className="text-neutral-400">→</span>
          <input type="date" data-testid="range-end" value={end} min={start || undefined}
            onChange={(e) => { setEnd(e.target.value); setPreset("custom"); }}
            className="rounded-sm border border-neutral-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-[#0055FF]" />
        </div>
      </div>

      <p className="text-xs text-neutral-500" data-testid="range-caption">
        {start || end
          ? <>Showing time-based metrics for <span className="font-semibold text-neutral-700">{fmtDisplay(start) || "the beginning"} – {fmtDisplay(end) || "today"}</span>, matched on job <span className="font-semibold">{jobField}</span> date. Snapshot tiles (Total Assets, Technicians, Active Clients) and Asset Intelligence always show live totals.</>
          : <>Showing <span className="font-semibold text-neutral-700">all-time</span> data. Snapshot tiles and Asset Intelligence always show live totals.</>}
      </p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const def = PRESETS.find((p) => p.id === "12m").range();
  const [start, setStart] = useState(def[0]);
  const [end, setEnd] = useState(def[1]);
  const [preset, setPreset] = useState("12m");
  const [jobField, setJobField] = useState("requested");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/summary", { params: { start, end, job_date_field: jobField } });
      setData(data);
    } catch {
      setData(false);
    } finally {
      setLoading(false);
    }
  }, [start, end, jobField]);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout wide>
      <div className="flex flex-wrap items-end justify-between gap-4" data-testid="dashboard-page">
        <div>
          <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Overview</p>
          <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">
            Welcome, {(user?.name || "there").split(" ")[0]}.
          </h1>
        </div>
      </div>

      <DateRangeBar preset={preset} setPreset={setPreset} start={start} end={end}
        setStart={setStart} setEnd={setEnd} jobField={jobField} setJobField={setJobField} loading={loading} coverage={data?.data_coverage} />

      {!data ? (
        <div className="mt-10 flex h-40 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
      ) : (
        <>
          <RateLimitBanner usage={data.aroflo_usage} jobsSource={data.jobs_source} />
          {/* Tiles */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Tile testid="tile-clients" icon={Users} label="Active Clients" value={data.tiles.active_clients.toLocaleString()} accent={{ bg: "bg-[#0055FF]/10", text: "text-[#0055FF]" }} onClick={() => navigate("/clients")} />
            <Tile testid="tile-assets" icon={Cube} label="Total Assets" value={data.tiles.assets.toLocaleString()} accent={{ bg: "bg-[#059669]/10", text: "text-[#059669]" }} onClick={() => navigate("/asset-list")} />
            <Tile testid="tile-technicians" icon={Wrench} label="Technicians" value={data.tiles.technicians.toLocaleString()} accent={{ bg: "bg-[#7C3AED]/10", text: "text-[#7C3AED]" }} onClick={() => navigate("/technicians")} />
            <Tile testid="tile-active-jobs" icon={Briefcase} label="Active Aroflo Jobs" value={(data.tiles.active_jobs ?? 0).toLocaleString()} accent={{ bg: "bg-[#0EA5E9]/10", text: "text-[#0EA5E9]" }} onClick={() => navigate("/jobs")} ranged />
            <Tile testid="tile-maximo" icon={ClipboardText} label="Maximo Work Orders" value={data.tiles.maximo_work_orders.toLocaleString()} accent={{ bg: "bg-[#D97706]/10", text: "text-[#D97706]" }} onClick={() => navigate("/maximo")} />
          </div>

          {/* Leaderboards */}
          {data.jobs_source === "aroflo_v1" && (
            <p className="mt-6 mb-1 inline-flex items-center gap-1.5 rounded-sm bg-[#D97706]/10 px-2.5 py-1 text-xs font-semibold text-[#D97706]" data-testid="jobs-source-v1">
              <Clock size={12} weight="bold" /> Job data via Aroflo v1 (legacy) — v2 daily limit reached
            </p>
          )}
          <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Leaderboard title="Top Clients by Jobs" icon={Briefcase} metric="clients-jobs" navigate={navigate} rows={data.top_clients_jobs} ranged
              render={(r) => (<><ClientName r={r} navigate={navigate} /><span className="shrink-0 font-heading text-sm font-black text-[#0055FF]">{r.job_count}</span></>)} />
            <Leaderboard title="Top Clients by Revenue" icon={CurrencyDollar} metric="clients-revenue" navigate={navigate} rows={data.top_clients_revenue} ranged
              render={(r) => (<><ClientName r={r} navigate={navigate} /><span className="shrink-0 font-heading text-sm font-black text-[#059669]">{fmtMoney(r.revenue)}</span></>)} />
            <Leaderboard title="Top Technicians by Jobs" icon={Wrench} metric="technicians-jobs" navigate={navigate} rows={data.top_technicians_jobs} ranged
              render={(r) => (<><TechName r={r} navigate={navigate} /><span className="shrink-0 font-heading text-sm font-black text-[#7C3AED]">{r.job_count}</span></>)} />
          </div>

          {/* Asset Intelligence Warnings */}
          <div className="mt-8" data-testid="intel-warnings">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Warning size={22} weight="fill" className="text-[#D97706]" />
                <h2 className="font-heading text-2xl font-black tracking-tighter text-[#0A0A0A]">Asset Intelligence Warnings</h2>
                <span className="rounded-sm bg-[#D97706]/10 px-2 py-0.5 text-xs font-semibold text-[#D97706]">{data.warnings_total}</span>
              </div>
              <button data-testid="warnings-viewall" onClick={() => navigate("/leaderboard/warnings")}
                className="inline-flex items-center gap-1 text-sm font-semibold text-[#0055FF] hover:underline">
                View full list <ArrowRight size={14} weight="bold" />
              </button>
            </div>
            <p className="mt-1 text-sm text-neutral-600">Assets flagged High/Critical or due for renewal within a year — prioritise these for inspection or replacement.</p>
            <div className="mt-3 overflow-x-auto border border-neutral-200 bg-white shadow-sm rounded-sm">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                    <th className="px-4 py-3 text-left">Asset</th>
                    <th className="px-4 py-3 text-left">Site</th>
                    <th className="px-4 py-3 text-left">Priority</th>
                    <th className="px-4 py-3 text-right">Health</th>
                    <th className="px-4 py-3 text-right">RUL (yrs)</th>
                    <th className="px-4 py-3 text-right">Renew by</th>
                    <th className="px-4 py-3 text-right">Est. cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.warnings.map((w, i) => (
                    <tr key={i} data-testid={`warning-row-${i}`} className="border-b border-neutral-100 even:bg-neutral-50/60">
                      <td className="px-4 py-3 font-semibold text-[#0A0A0A]">{w.asset_name}</td>
                      <td className="max-w-[260px] truncate px-4 py-3 text-neutral-600" title={w.site}>{w.site || "—"}</td>
                      <td className="px-4 py-3"><span className={`inline-flex rounded-sm px-2 py-0.5 text-xs font-bold ${BAND[w.priority_band] || "bg-neutral-100 text-neutral-500"}`}>{w.priority_band}</span></td>
                      <td className="px-4 py-3 text-right font-mono">{w.health_score}</td>
                      <td className="px-4 py-3 text-right font-mono">{w.remaining_useful_life}</td>
                      <td className="px-4 py-3 text-right font-mono">{w.recommended_renewal_year}</td>
                      <td className="px-4 py-3 text-right font-mono">{fmtMoney(w.replacement_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-neutral-400">
              <MapPin size={12} /> Sourced from the Asset Intelligence model. Live condition reports feed in as technicians complete inspections.
            </p>
          </div>
        </>
      )}
    </Layout>
  );
}
